import { Mixin } from 'ts-mixer';
import videojs, { VideoJsPlayer } from 'video.js';
import 'videojs-contrib-ads';
import 'videojs-ima';

import '@src/components/CustomBigPlayButton';
import '@src/components/CustomErrorDisplay';
import '@src/components/CustomFullscreenToggle';
import '@src/components/CustomMenuButton';
import '@src/components/CustomMuteToggle';
import '@src/components/CustomPlayToggle';
import '@src/components/CustomPosterImage';
import '@src/components/CustomSeekBar';
import '@src/components/CustomSubCapsButton';
import '@src/components/CustomTextTrackDisplay';
import '@src/components/CustomVolumeControl';
import '@src/components/FloatingCloseButton';
import '@src/components/FloatingDurationDisplay';
import '@src/components/FloatingInfo';
import '@src/components/QualityLevelsMenuButton';
import '@src/components/ToastDisplay';
import '@src/components/UserInactiveOverlay';
import '@src/hooks/beforeSetup';
import '@src/hooks/setup';
import '@src/localization/zh-TW';
import '@src/plugins/AdsManagerPlugin';
import type AdsManagerPlugin from '@src/plugins/AdsManagerPlugin';
import '@src/plugins/ContextPlugin';
import '@src/plugins/Controller';
import type Controller from '@src/plugins/Controller';
import '@src/plugins/EventListenerPlugin';
import '@src/plugins/EventManagerPlugin';
import '@src/plugins/QualityLevelsManagerPlugin';
import '@src/plugins/UIManagerPlugin';
import type { UserPlayerOptions } from '@src/types/player';
import { OmitFirstArg } from '@src/types/utils';
import { detectDebugHelper } from '@src/utils/debug';

import type PlayerManager from './PlayerManager';
import ControlPlayerMixin from './mixins/ControlPlayerMixin';
import CreatePlayerMixin from './mixins/CreatePlayerMixin';

export type IPlayerProxyBase = VideoJsPlayer;

export type IPlayerProxy = IPlayerProxyBase & {
  playerManager?: PlayerManager;
  restoreContainerElement?: (restoredEl: Element) => void;
  toast: OmitFirstArg<ControlPlayerMixin['toast']>;
  setSourceWithAdTag: OmitFirstArg<ControlPlayerMixin['setSourceWithAdTag']>;
  isAdPlaying: AdsManagerPlugin['isAdPlaying'];
  isInAdBreak: AdsManagerPlugin['isInAdBreak'];
  pauseVideoOrAd: Controller['pauseVideoOrAd'];
  requestAdsOrPlay: Controller['requestAdsOrPlay'];
  unmuteAndSetToLastVolume: Controller['unmuteAndSetToLastVolume'];
  handleRequestAdsOnClickToPlay: Controller['handleRequestAdsOnClickToPlay'];
  handleSetToLastVolumeOnClickToPlay: Controller['handleSetToLastVolumeOnClickToPlay'];
};

export type IPlayerProxyAttachPayload = Pick<
  IPlayerProxy,
  'playerManager' | 'restoreContainerElement'
>;

detectDebugHelper();

class PlayerWrapper extends Mixin(CreatePlayerMixin, ControlPlayerMixin) {
  proxy?: IPlayerProxy;
  playerManager?: PlayerManager;
  restoreContainerElement?: (restoredEl: Element) => void;

  static async from(
    containerElementOrId: string | HTMLElement | HTMLVideoElement,
    options?: UserPlayerOptions,
    ready?: videojs.ReadyCallback,
    playerManager?: PlayerManager
  ): Promise<{ proxy?: IPlayerProxy; vjsPlayer?: VideoJsPlayer } | void> {
    const wrapper = new PlayerWrapper();

    return wrapper.createPlayerAndProxy(
      containerElementOrId,
      options,
      ready,
      playerManager
    );
  }

  public async createPlayerAndProxy(
    containerElementOrId: string | HTMLElement | HTMLVideoElement,
    options?: UserPlayerOptions,
    ready?: videojs.ReadyCallback,
    playerManager?: PlayerManager
  ) {
    const { vjsPlayer, restoreContainerElement } =
      (await this.createPlayer(containerElementOrId, options, ready)) ?? {};

    return {
      vjsPlayer,
      proxy:
        vjsPlayer &&
        this.createProxy(vjsPlayer, {
          restoreContainerElement,
          playerManager,
        }),
    };
  }

  public createProxy(
    vjsPlayer: VideoJsPlayer,
    props: IPlayerProxyAttachPayload
  ) {
    const _proxy = new Proxy(
      vjsPlayer,
      createProxyHandler(this)
    ) as IPlayerProxy;

    (Object.keys(props) as (keyof IPlayerProxyAttachPayload)[]).forEach(
      (key) => {
        if (props[key]) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          _proxy[key] = props[key];
        }
      }
    );

    this.proxy = _proxy;

    return _proxy;
  }
}

const createProxyHandler = (wrapper: PlayerWrapper) => {
  return {
    get(vjsPlayer: VideoJsPlayer, prop: string | symbol /*, receiver */) {
      if (prop === 'playerManager') {
        return wrapper.playerManager;
      }

      if (prop === 'vjsPlayer') {
        return vjsPlayer;
      }

      if (prop === 'setSourceWithAdTag') {
        return ((...args) =>
          wrapper.setSourceWithAdTag(
            vjsPlayer,
            ...args
          )) as IPlayerProxy['setSourceWithAdTag'];
      }

      if (prop === 'pauseVideoOrAd') {
        return vjsPlayer.controller?.pauseVideoOrAd.bind(vjsPlayer.controller);
      }

      if (prop === 'requestAdsOrPlay') {
        return vjsPlayer.controller?.requestAdsOrPlay.bind(
          vjsPlayer.controller
        );
      }

      if (prop === 'unmuteAndSetToLastVolume') {
        return vjsPlayer.controller?.unmuteAndSetToLastVolume.bind(
          vjsPlayer.controller
        );
      }

      if (prop === 'handleRequestAdsOnClickToPlay') {
        return vjsPlayer.controller?.handleRequestAdsOnClickToPlay.bind(
          vjsPlayer.controller
        );
      }

      if (prop === 'handleSetToLastVolumeOnClickToPlay') {
        return vjsPlayer.controller?.handleSetToLastVolumeOnClickToPlay.bind(
          vjsPlayer.controller
        );
      }

      if (prop === 'isAdPlaying') {
        return vjsPlayer.adsManagerPlugin?.isAdPlaying.bind(
          vjsPlayer.adsManagerPlugin
        );
      }

      if (prop === 'isInAdBreak') {
        return vjsPlayer.adsManagerPlugin?.isInAdBreak.bind(
          vjsPlayer.adsManagerPlugin
        );
      }

      if (prop === 'toast') {
        return ((...args) =>
          wrapper.toast(vjsPlayer, ...args)) as IPlayerProxy['toast'];
      }

      if (prop === 'dispose') {
        return () => {
          wrapper.disposePlayer(vjsPlayer);
        };
      }

      return vjsPlayer[prop as keyof VideoJsPlayer];
    },

    set(player: VideoJsPlayer, prop: string | symbol, value: any) {
      if (
        ['playerManager', 'restoreContainerElement'].includes(prop as string)
      ) {
        wrapper[prop as 'playerManager' | 'restoreContainerElement'] = value;

        return true;
      }

      player[prop as unknown as keyof VideoJsPlayer] = value;
      return true;
    },
  };
};

export default PlayerWrapper;
